import { useState } from "react";
import { DateTime } from "luxon";
import { AlertTriangle, Database, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDatabaseRefresh } from "@/hooks/useDatabaseRefresh";

function relative(iso: string): string {
  const dt = DateTime.fromISO(iso);
  return dt.isValid ? (dt.toRelative() ?? "-") : "-";
}

export function DatabaseRefreshCard() {
  const { status, start, starting, startError } = useDatabaseRefresh();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const running = status?.running ?? false;
  const pct =
    status && status.totalDays > 0 ? Math.round((status.daysDone / status.totalDays) * 100) : 0;

  return (
    <div className="flex flex-col gap-4 rounded-lg border bg-card p-4 shadow-sm">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="flex gap-3">
          <Database className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
          <div>
            <h2 className="text-[15px] font-semibold tracking-tight">Scheduling data</h2>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              Empties the scheduling tables and re-pulls every day from Microsoft Fabric, one month
              at a time. Rows deleted at source are removed here too, which a normal sync never does
              — it can only add or overwrite. Use this after the source tables are restated.{" "}
              <strong className="font-medium text-foreground">
                The dashboard has no data until it finishes
              </strong>
              , so run it when nobody is using it.{" "}
              <span className="block pt-2">
                It rebuilds every date the dashboard can show for today — roughly the last two
                years, covering all seven periods and their prior-year comparisons. Older dates are
                dropped and fetched again on demand the moment anyone selects an As-of date that
                needs them.
              </span>
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="destructive"
          disabled={running || starting}
          onClick={() => setConfirmOpen(true)}
          className="shrink-0"
        >
          {running || starting ? <Loader2 className="animate-spin" /> : null}
          {running ? "Refreshing…" : "Refresh Database"}
        </Button>
      </div>

      {running && (
        <div className="flex flex-col gap-1.5">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-destructive transition-[width] duration-500 ease-out"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {status!.daysDone.toLocaleString()} of {status!.totalDays.toLocaleString()} days · {pct}
            % · {status!.rowsUpserted.toLocaleString()} rows written
            {status!.currentSpan ? ` · ${status!.currentSpan}` : ""}
          </p>
        </div>
      )}

      {!running && status?.error && (
        <p className="flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Last refresh failed{status.finishedAt ? ` ${relative(status.finishedAt)}` : ""}:{" "}
            {status.error}
          </span>
        </p>
      )}

      {!running && !status?.error && status?.finishedAt && (
        <p className="text-xs text-muted-foreground">
          Last refresh completed {relative(status.finishedAt)} —{" "}
          {status.rowsUpserted.toLocaleString()} rows across {status.totalDays.toLocaleString()}{" "}
          days.
        </p>
      )}

      {startError && (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {startError.message}
        </p>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Refresh the entire scheduling cache?</DialogTitle>
            <DialogDescription>
              The scheduling tables are emptied, then roughly the last two years are re-fetched from
              Fabric in month-sized batches — every date the dashboard can show for today. The
              dashboard has no data until it finishes, and it can take many minutes. You can leave
              this page; the job keeps running on the server.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                setConfirmOpen(false);
                start();
              }}
            >
              Refresh Database
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
